import { useEffect, useState } from "react";
import DSDialog from "views/system-design/DSDialog";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogActions from "views/system-design/DSDialogActions";
import DSButton from "views/system-design/DSButton";
import DSBox from "views/system-design/DSBox";
import DSSwitch from "views/system-design/DSSwitch";
import DSTextField from "views/system-design/DSTextField";
import DSTypography from "views/system-design/DSTypography";
import { AlertPolicy, AlertUser } from "../../../api/alertPolicies";
import {
  useCreateAlertPolicy,
  useUpdateAlertPolicy,
  useDeleteAlertPolicy,
} from "../hooks/useAlertPolicies";
import AdminUserPicker from "./AdminUserPicker";

interface AlertPolicyModalProps {
  // null = closed, {} = create, populated = edit
  policy: AlertPolicy | Record<string, never> | null;
  onClose: () => void;
}

const AlertPolicyModal = ({ policy, onClose }: AlertPolicyModalProps) => {
  const editing = policy && "id" in policy ? (policy as AlertPolicy) : null;
  const [name, setName] = useState("");
  const [adminUsers, setAdminUsers] = useState<AlertUser[]>([]);
  const [defaultForClockMonitors, setDefaultForClockMonitors] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const monitorCount = editing?.monitorCount ?? 0;
  const inUse = monitorCount > 0;

  const { mutate: create, isPending: isCreating } = useCreateAlertPolicy();
  const { mutate: update, isPending: isUpdating } = useUpdateAlertPolicy();
  const { mutate: remove, isPending: isDeleting } = useDeleteAlertPolicy();

  useEffect(() => {
    setName(editing?.name ?? "");
    setAdminUsers(editing?.adminUsers ?? []);
    setDefaultForClockMonitors(editing?.defaultForClockMonitors ?? false);
    setConfirmDelete(false);
  }, [policy]);

  if (!policy) return null;

  const handleSave = () => {
    const data = { name, adminUsers, defaultForClockMonitors };
    if (editing) {
      update({ id: editing.id, data }, { onSuccess: onClose });
    } else {
      create(data, { onSuccess: onClose });
    }
  };

  return (
    <DSDialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DSDialogTitle>
        {editing ? "Edit alert policy" : "New alert policy"}
      </DSDialogTitle>
      <DSDialogContent>
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <DSTextField
            label="Name"
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="oncall-critical"
          />
          <AdminUserPicker value={adminUsers} onChange={setAdminUsers} />
          <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DSSwitch
              size="small"
              checked={defaultForClockMonitors}
              onChange={(e) => setDefaultForClockMonitors(e.target.checked)}
            />
            <DSBox>
              <DSTypography variant="body2">
                Default for clock monitors
              </DSTypography>
              <DSTypography variant="caption" color="text.secondary">
                Attached to clock monitors auto-created by failure reports. Only
                one policy can be the default — enabling it here removes it from
                the current one.
              </DSTypography>
            </DSBox>
          </DSBox>
          {editing && (
            <DSTypography variant="caption" color="text.secondary">
              {inUse
                ? `Used by ${monitorCount} monitor${monitorCount === 1 ? "" : "s"} — reassign them before deleting.`
                : "Not used by any monitor."}
            </DSTypography>
          )}
        </DSBox>
      </DSDialogContent>
      <DSDialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <DSBox sx={{ display: "flex", gap: 1 }}>
          {editing &&
            (confirmDelete ? (
              <>
                <DSButton
                  color="error"
                  variant="contained"
                  size="small"
                  disabled={isDeleting}
                  onClick={() => remove(editing.id, { onSuccess: onClose })}
                >
                  Confirm delete
                </DSButton>
                <DSButton size="small" onClick={() => setConfirmDelete(false)}>
                  Cancel
                </DSButton>
              </>
            ) : (
              <DSButton
                color="error"
                size="small"
                disabled={inUse}
                title={
                  inUse
                    ? `Used by ${monitorCount} monitor(s) — reassign them first`
                    : undefined
                }
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </DSButton>
            ))}
        </DSBox>
        <DSBox sx={{ display: "flex", gap: 1 }}>
          <DSButton size="small" onClick={onClose}>
            Cancel
          </DSButton>
          <DSButton
            variant="contained"
            size="small"
            disabled={isCreating || isUpdating || !name.trim()}
            onClick={handleSave}
          >
            Save
          </DSButton>
        </DSBox>
      </DSDialogActions>
    </DSDialog>
  );
};

export default AlertPolicyModal;
